import { zepto } from 'zepto-webpack'
import { h, resolveDynamicComponent, vShow, withDirectives } from 'vue'
import { innerText } from '@/common/util/dom'
import { ensureUniqId } from './svg'

const mergeClassNames = (...values) => {
  return values.flatMap(value => {
    if (!value) return []
    return Array.isArray(value) ? value : [value]
  })
}

const SLOT_MARKER = '__kryptonSlotMarker'

const isSlotDirectiveName = name => {
  return (
    name === 'v-slot' ||
    name.indexOf('v-slot:') === 0 ||
    name.indexOf('#') === 0
  )
}

const getSlotName = $element => {
  if (!$element || !$element.attributes) return null

  for (let i = 0; i < $element.attributes.length; i++) {
    const attr = $element.attributes[i]
    if (attr.name === 'v-slot') {
      return attr.value || 'default'
    }
    if (attr.name.indexOf('v-slot:') === 0) {
      return attr.name.replace('v-slot:', '') || 'default'
    }
    if (attr.name.indexOf('#') === 0) {
      return attr.name.slice(1) || 'default'
    }
    if (attr.name === 'slot') {
      return attr.value || 'default'
    }
  }

  return null
}

const createSlotsObject = children => {
  if (!Array.isArray(children)) return children

  const slotEntries = children.filter(
    child => child && child[SLOT_MARKER] === true
  )

  const defaultChildren = children.filter(
    child => !(child && child[SLOT_MARKER] === true)
  )

  // If there are no named slots and no default content, return the children as-is
  if (!slotEntries.length && !defaultChildren.length) return children

  const slots = {}

  slotEntries.forEach(entry => {
    slots[entry.name] = entry.render
  })

  if (defaultChildren.length) {
    slots.default = () => defaultChildren
  }

  return slots
}

const setEventHandler = (props, eventName, handler) => {
  if (!handler) return

  const existingHandlers = props[eventName]
  if (!existingHandlers) {
    props[eventName] = [handler]
  } else {
    props[eventName] = [...existingHandlers, handler]
  }
}

const getNativeModelProp = $element => {
  const tagName = $element.tagName?.toLowerCase()
  const type = $element.getAttribute?.('type')
  if (tagName === 'input' && ['checkbox', 'radio'].includes(type)) {
    return 'checked'
  }

  return 'value'
}

const updateModelValue = (self, key, $event) => {
  const $el = $event.target
  if ($el?.type === 'radio') {
    self[key] = $el.checked ? $el.value : ''
  } else if ($el?.type === 'checkbox') {
    self[key] = !!$el.checked
  } else {
    self[key] = $el?.value
  }
}

const normalizeModelArg = value => {
  return value.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
}

const applyModelBinding = (config, self, $element, attr) => {
  const modelMatch = attr.name.match(/^v-model(?::(.+))?$/)
  if (!modelMatch) return false

  const modelName = modelMatch[1] ? normalizeModelArg(modelMatch[1]) : null
  const tagName = $element.tagName?.toLowerCase()
  const inputType = $element.getAttribute?.('type')
  const isCheckable =
    tagName === 'input' && ['checkbox', 'radio'].includes(inputType)
  const isNative = ['input', 'textarea', 'select'].includes(tagName)

  if (modelName) {
    config.props[modelName] = self[attr.value]
    setEventHandler(config.props, `onUpdate:${modelName}`, value => {
      self[attr.value] = value
    })
    setEventHandler(config.props, `onUpdate:${modelMatch[1]}`, value => {
      self[attr.value] = value
    })
    return true
  }

  if (isNative) {
    // cast to a real boolean: Vue treats a falsy but non-null "checked"
    let modelValue = self[attr.value]
    if (inputType === 'radio') {
      modelValue = modelValue === $element.getAttribute('value')
    } else if (isCheckable) {
      modelValue = !!modelValue
    }
    config.props[getNativeModelProp($element)] = modelValue
    if (!isCheckable) {
      setEventHandler(config.props, 'onInput', $event => {
        updateModelValue(self, attr.value, $event)
      })
    }
    setEventHandler(config.props, 'onChange', $event => {
      updateModelValue(self, attr.value, $event)
    })
  } else {
    // Vue component: use modelValue + onUpdate:modelValue
    config.props['modelValue'] = self[attr.value]
    setEventHandler(config.props, 'onUpdate:modelValue', value => {
      self[attr.value] = value
    })
  }

  return true
}

/*
  The createElement has no option to pass directives, in vue3
  withDirectives has to be called
*/
const buildVNode = (component, config, children) => {
  const props = config.props || {}
  const vnode = h(component, props, createSlotsObject(children))
  if (!config.directives.length) return vnode

  return withDirectives(vnode, config.directives)
}

export const getCleanValue = (self, value) => {
  // Clean possible parenthesis
  if (value.indexOf('(') !== -1) {
    value = value.match(/\((.*)\)/)[1]
  }

  // 2 values with an or
  if (value.indexOf('||') !== -1) {
    const values = value.split('||')
    if (values.length === 2)
      return self[values[0].trim()] || self[values[1].trim()]
  }

  return self[value]
}

// Extract the key and value of a smple object
export const extractAttribute = (self, object, value) => {
  const regex = new RegExp(`{\s?'(.*)'\s?:\s?(.*)\s?}`)
  const match = value.match(regex)
  if (match.length === 3) {
    object[match[1]] = getCleanValue(self, match[2])
  }
}

export const extractObjectAttribute = (self, value) => {
  const object = {}
  // Simple object
  if (value.indexOf(',') === -1) {
    // Get the data from object like bind
    extractAttribute(self, object, value)
    // Complex object
  } else {
    // Clean global {}
    value = value.match(/\{(.*)\}/)[1]
    value.split(',').forEach(attr => {
      // Add a {} to parse it as a simple object
      const simObj = `{${attr.trim()}}`
      extractAttribute(self, object, simObj)
    })
  }

  return object
}

export const parseToRenderConfig = (self, $element) => {
  const config = {
    props: {},
    directives: []
  }
  if (!$element) return config

  for (let i = 0; i < $element.attributes.length; i++) {
    const attr = $element.attributes[i]
    if (applyModelBinding(config, self, $element, attr)) {
      continue
    }

    switch (attr.name) {
      case 'class':
        config.props.class = mergeClassNames(config.props.class, attr.value)
        break
      case 'v-bind:class':
        // Check if the attribute is an object
        const regex = new RegExp('{.*}')
        const match = attr.value.match(regex)
        if (match.length) {
          config.props.class = mergeClassNames(
            config.props.class,
            extractObjectAttribute(self, attr.value)
          )
        }
        break
      case 'v-show':
        config.directives.push([vShow, self[attr.value]])
        break
      case 'v-on:input':
        setEventHandler(config.props, 'onInput', self[attr.value])
        break
      case 'v-on:mousedown':
        setEventHandler(config.props, 'onMousedown', self[attr.value])
        break
      case 'v-on:focus':
        setEventHandler(config.props, 'onFocus', self[attr.value])
        break
      case 'v-on:blur':
        setEventHandler(config.props, 'onBlur', self[attr.value])
        break
      default:
        if (isSlotDirectiveName(attr.name)) {
          break
        }
        if (attr.name.indexOf('v-bind:') === 0) {
          const attrName = attr.name.replace('v-bind:', '')
          config.props[attrName] = self[attr.value]
        } else {
          config.props[attr.name] = attr.value
        }
        break
    }
  }

  return config
}

export const parseChildrenToRender = (self, $element, childrenConf = []) => {
  if (!$element) return childrenConf

  // When passed a <template> the content is inserted in a DocumentFragment
  if ('content' in $element && !$element.childNodes.length)
    $element = $element.content

  for (let i = 0; i < $element.childNodes.length; i++) {
    const son = $element.childNodes[i]
    // Text node
    if (son.nodeName === '#text') {
      childrenConf.push(son.nodeValue)
    } else if (son.nodeName.indexOf('#') === -1 && son.tagName) {
      // Dom element node
      const text = innerText(son)
      const content = text ? text.trim() : ''
      const hasFragment = !!(
        son.content &&
        son.content.children &&
        son.content.children.length
      )
      const hasChildren = !!(
        (son.children && son.children.length) ||
        hasFragment
      )
      const isAttr = son.getAttribute('is')
      const slotName = getSlotName(son)
      const hasIf = !!son.attributes['v-if']
      if (hasIf && !self[son.attributes['v-if']]) continue

      const children = hasChildren
        ? parseChildrenToRender(self, son)
        : content || ''

      // Vue 3 slot syntax: <template v-slot:name>...</template>
      if (slotName !== null && son.tagName.toLowerCase() === 'template') {
        childrenConf.push({
          [SLOT_MARKER]: true,
          name: slotName,
          render: () => children
        })
        continue
      }

      childrenConf.push(
        buildVNode(
          resolveDynamicComponent(isAttr || son.tagName.toLowerCase()),
          parseToRenderConfig(self, son),
          children
        )
      )
    }
  }

  return childrenConf
}

export const render = (self, $element) => {
  if ($element instanceof zepto.Z) {
    $element = $element[0]
  }
  const isAttr = $element.getAttribute('is')
  return buildVNode(
    resolveDynamicComponent(isAttr || 'div'),
    parseToRenderConfig(self, $element),
    parseChildrenToRender(self, $element)
  )
}

/**
 * Introduced with SplitPayment button label.
 *
 * It is a simpler version of the functions above, not depending on Zepto object.
 *
 * !! IT SHOULD NOT SUPPORT VUE DIRECTIVES !!
 * !! AS THE STORE TEMPLATES CAN BE MODIFIED BY DEVELOPERS !!
 *
 * The goal is to dynamically compile button.template & button.animation into
 * a virtual DOM object in order to maintain the exact DOM result for the
 * normal label, and used the newly added component SplitPaymentButtonLabel.
 *
 * @since KJS-4366
 */
export class HtmlToVueCompiler {
  /**
   * Vue 3 render helper is imported directly so callers do not need to pass
   * it through every constructor.
   */
  constructor() {
    this.domParser = new DOMParser()
  }

  render(html) {
    const document = this.domParser.parseFromString(html, 'text/html')

    return this.compileNode(document.body.firstChild)
  }

  /**
   * @param {Node} node
   * @returns {VNode}
   */
  compileNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase()
      return h(
        resolveDynamicComponent(tagName),
        this.parseConfiguration(node),
        [...node.childNodes].map(node => this.compileNode(node))
      )
    }
    return ''
  }

  /**
   * @param {Element} element
   * @returns {object}
   */
  parseConfiguration(element) {
    const configuration = {}

    for (const attr of element.attributes) {
      const { name, value } = attr
      if (this.isVueDirective(name)) continue
      configuration[name] = value
    }

    return configuration
  }

  isVueDirective(str) {
    return str.startsWith(':') || str.startsWith('v-')
  }
}

/**
 * Automatically set the right attribute to svg and its descendant based on
 * whether it is decorative or informative.
 *
 * For more details see:
 *  https://design-system.w3.org/styles/svg-icons.html#svg-accessibility
 *
 * Example #1 (decorative)
 * <svg aria-hidden="true" focusable="false" ... >
 *     <!-- child elements of the inline SVG would go here -->
 * </svg>
 *
 * Example #2 (informative)
 * <svg role="img" focusable="false" aria-label="Accessible Name">
 *     <title>Accessible Name</title>
 *     <!-- child elements of the inline SVG would go here -->
 *     <path role="presentation" ...> ...</path>
 * </svg>
 *
 */
export class SvgToVueCompiler extends HtmlToVueCompiler {
  constructor() {
    super()
  }

  /**
   * @param {string} html template
   * @param {Object} opts
   * @param {boolean} [opts.decorative] (default: true) whether svg is decorative or not
   * @param {string|null} [opts.title] <title> value to be set. Also set aria-label if setAriaLabel == true
   * @param {boolean} [opts.setAriaLabel] (default: true) whether aria-label should be automatically set or not
   * @returns {VNode}
   */
  render(html, opts = {}) {
    // each instance needs differents ids
    const newHtml = ensureUniqId(html)
    opts = { decorative: true, title: null, setAriaLabel: true, ...opts }
    const document = this.domParser.parseFromString(newHtml, 'text/html')

    return this.compileNode(opts, document.body.firstChild)
  }

  /**
   * @param {Object} context
   * @param {Boolean} context.decorative
   * @param {String | null} context.title
   * @param {Node} node
   * @returns {VNode}
   */
  compileNode(context, node, _level = 0) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName
      const configuration = this.parseConfiguration(node)
      const children = [...node.childNodes]

      if (context.decorative) {
        if (0 === _level) {
          this.setAttribute(configuration, 'focusable', 'false')
          this.setAriaHidden(configuration)
        }
      } else {
        if (0 === _level) {
          this.setAttribute(configuration, 'focusable', 'false')
          this.setRole(configuration, 'img')
          if (context.title) {
            if (context.setAriaLabel) {
              this.setLabel(configuration, context.title)
            }
            if (!this.hasTitleChild(children)) {
              const title = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'title'
              )
              children.unshift(title)
            }
          }
        } else if (!['text', 'title', 'desc'].includes(tagName)) {
          this.setRole(configuration, 'presentation')
        } else if ('title' === tagName && context.title) {
          // Override title
          children.length = 0
          children.push(document.createTextNode(context.title))
        }
      }

      return h(
        resolveDynamicComponent(tagName),
        configuration,
        children.map(node => this.compileNode(context, node, 1 + _level))
      )
    }
    return ''
  }

  /**
   * @param {Node[]} childNodes
   * @returns {boolean}
   */
  hasTitleChild(childNodes) {
    return childNodes.some(node => node.tagName === 'title')
  }

  /**
   * @param {Object} configuration
   * @param {String} name
   * @param {Unknown} value
   * @returns {Object} configuration
   */
  setAttribute(configuration, name, value) {
    configuration[name] = value
    return configuration
  }

  setAriaHidden(configuration) {
    return this.setAttribute(configuration, 'aria-hidden', 'true')
  }

  setLabel(configuration, value) {
    return this.setAttribute(configuration, 'aria-label', value)
  }

  setRole(configuration, value) {
    return this.setAttribute(configuration, 'role', value)
  }
}
